import React, { FC, useCallback, useEffect, useState } from 'react';
import { ICustomizerProps } from '../../pages';
import { AnyObject, APP_STYLES, DEFAULT_STYLES, urlToParams } from '../../utils/basic';

export interface IParams {
  pathParams: { pathPrefix?: string; [key: string]: any };
  queryParams: AnyObject;
  otherQueryParams?: AnyObject;
}

export interface IUrl {
  pathString: string;
  queryString: string;
  ring_url: string;
}

export interface ICustomizerHOCProps {
  match: any;
  location: any;
  history: any;
  type: number;
  ring_url?: string;
  otherQueryParams?: AnyObject;
}

const RingCustomizerHOC = (Component: FC<ICustomizerProps>) => {
  return function HOC(props: ICustomizerHOCProps) {
    const { match, location, type, ring_url, otherQueryParams } = props;

    const [params, setParams] = useState<IParams>({
      pathParams: {},
      queryParams: {}
    });

    const [url, setUrl] = useState<IUrl>({
      pathString: '',
      queryString: '',
      ring_url: ''
    });

    useEffect(() => {
      function handleResize() {
        const vh = window.innerHeight;
        document.documentElement.style.setProperty('--vh', `${vh}px`);
      }
      window.addEventListener('resize', handleResize);
      handleResize();
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }, []);

    useEffect(() => {
      const {
        pathParams: { pathPrefix }
      } = params;
      if (pathPrefix) {
        const styles = APP_STYLES[pathPrefix] || DEFAULT_STYLES;

        document.documentElement.style.setProperty('--brand-color', styles.brandColor);
        document.documentElement.style.setProperty('--accent-color', styles.accentColor);
        document.documentElement.style.setProperty('--cta-hover-bg-color', styles.ctaHoverBgColor);
        document.documentElement.style.setProperty('--cta-hover-text-color', styles.ctaHoverTextColor);

        document.documentElement.style.setProperty('--primary-bg-color', styles.primaryBgColor);
        document.documentElement.style.setProperty('--secondary-bg-color', styles.secondaryBgColor);
        document.documentElement.style.setProperty('--sidebar-bg-color', styles.sidebarBgColor);
        document.documentElement.style.setProperty('--tab-active-bg-color', styles.tabActiveBgColor);
        document.documentElement.style.setProperty('--tab-border-color', styles.tabBorderColor);

        document.documentElement.style.setProperty('--size-option-bg-color', styles.sizeOptionBgColor);
        document.documentElement.style.setProperty('--size-option-text-color', styles.sizeOptionTextColor);
        document.documentElement.style.setProperty('--size-option-border-color', styles.sizeOptionBorderColor);
        document.documentElement.style.setProperty('--size-option-radius', styles.sizeOptionRadius);
        document.documentElement.style.setProperty('--size-option-active-bg-color', styles.sizeOptionActiveBgColor);
        document.documentElement.style.setProperty(
          '--size-option-active-border-color',
          styles.sizeOptionActiveBorderColor
        );

        document.documentElement.style.setProperty('--scrollbar-thumb-color', styles.scrollbarThumbColor);
        document.documentElement.style.setProperty('--scrollbar-radius', styles.scrollbarRadius);

        document.documentElement.style.setProperty('--primary-color', styles.primaryColor);
        document.documentElement.style.setProperty('--secondary-color', styles.secondaryColor);
        document.documentElement.style.setProperty('--tertiary-color', styles.tertiaryColor);

        document.documentElement.style.setProperty('--primary-font-family', styles.primaryFont);
        document.documentElement.style.setProperty('--secondary-font-family', styles.secondaryFont);

        document.documentElement.style.setProperty('--brand-logo', styles.brandlogo);
        document.documentElement.style.setProperty('--brand-logo-secondary', styles.brandlogoSecondary);

        document.documentElement.style.setProperty('--primary-border-color', styles.primaryBorderColor);
        document.documentElement.style.setProperty('--secondary-border-color', styles.secondaryBorderColor);

        document.documentElement.style.setProperty('--primary-border-radius', styles.primaryBorderRadius);
        document.documentElement.style.setProperty('--secondary-border-radius', styles.secondaryBorderRadius);
        document.documentElement.style.setProperty('--tertiary-border-radius', styles.tertiaryBorderRadius);

        document.documentElement.style.setProperty('--rc-box-tab-case', styles.rcBoxTabCase);

        document.documentElement.style.setProperty('--sidebar-order', styles.sidebarOrder);
      }
    }, [params.pathParams.pathPrefix]); // if params, it will call everytime when option choose

    useEffect(() => {
      const url = ring_url || `${location.pathname}${location.search}`;
      if (url) {
        const { pathParams, queryParams: searchParams } = urlToParams(url);
        const { product_id, bestSeller, instore, ...queryParams } = searchParams;
        setParams({
          pathParams,
          queryParams,
          otherQueryParams: {
            ...(product_id ? { product_id } : {}),
            ...(bestSeller ? { bestSeller } : {}),
            ...(instore ? { instore } : {}),
            ...otherQueryParams
          }
        });
        if (type === 1) {
          const { profile, width, ring_materials } = pathParams;
          const pathString = `/rings/profiles/${profile}/widths/${width}/ring_materials/${ring_materials}`;
          const queryString = new URLSearchParams(queryParams).toString();
          setUrl({
            pathString,
            queryString,
            ring_url: `${pathString}?${queryString}`
          });
        } else if (type === 2) {
          const { styles, ring_materials, color_arrangements, stone_quantities, variants } = pathParams;
          const pathString = `/women/rings/styles/${styles}/ring_materials/${ring_materials}/color_arrangements/${color_arrangements}/stone_quantities/${stone_quantities}/variants/${variants}`;
          const queryString = new URLSearchParams(queryParams).toString();
          setUrl({
            pathString,
            queryString,
            ring_url: `${pathString}?${queryString}`
          });
        } else if (type === 3) {
          const { ring_type, ring_materials } = pathParams;
          const pathString = `/rings/${ring_type}/ring_materials/${ring_materials}`;
          const queryString = new URLSearchParams(queryParams).toString();
          setUrl({
            pathString: pathString,
            queryString,
            ring_url: `${pathString}?${queryString}`
          });
        } else if (type === 4) {
          const { ring_type, profile, width, ring_materials } = pathParams;
          const pathString = `/rings/${ring_type}/profiles/${profile}/widths/${width}/ring_materials/${ring_materials}`;
          const queryString = new URLSearchParams(queryParams).toString();
          setUrl({
            pathString,
            queryString,
            ring_url: `${pathString}?${queryString}`
          });
        }
      }
    }, [ring_url, type, location, match, otherQueryParams]);

    return <Component {...props} params={params} setParams={setParams} url={url} isQueryHandling={!!ring_url} />;
  };
};

export default RingCustomizerHOC;
